import { create } from 'zustand';
import { AuthUser, UserRole } from '@/types/auth';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/services/firebase';
import { rbacApi } from '@/services/rbacApi';
import { clearStoredAuth, handleUnauthorizedSession } from '../utils/authSession';
import toast from 'react-hot-toast';

interface UserPermissions {
    [resource: string]: string[]; // e.g., products: ['read', 'create', 'update']
}

interface AuthStoreUser extends AuthUser {
    permissions?: UserPermissions;
    pages?: string[];
    menuItems?: string[];
}

interface AuthStore {
    user: AuthStoreUser | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    idToken: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    setUser: (user: AuthStoreUser | null) => void;
    hasPermission: (requiredRole: UserRole) => boolean;
    canAccessPage: (page: string) => boolean;
    hasResourcePermission: (resource: string, action: string) => boolean;
    initAuth: () => void;
}

const ADMIN_PERMISSIONS: UserPermissions = {
    products: ['read', 'create', 'update', 'delete'],
    customers: ['read', 'create', 'update', 'delete'],
    orders: ['read', 'create', 'update', 'delete'],
    vendors: ['read', 'create', 'update', 'delete'],
    couriers: ['read', 'create', 'update', 'delete'],
    accounting: ['read', 'create', 'update', 'delete'],
    'extra-products': ['read', 'create', 'update', 'delete'],
    taxonomy: ['read', 'create', 'update', 'delete'],
    settings: ['read', 'update'],
    'delivery-areas': ['read', 'create', 'update', 'delete'],
    coupons: ['read', 'create', 'update', 'delete'],
    'special-dates': ['read', 'create', 'update', 'delete'],
    banners: ['read', 'create', 'update', 'delete'],
    reviews: ['read', 'create', 'update', 'delete'],
    notifications: ['read', 'update'],
    pages: ['read', 'create', 'update', 'delete'],
    'payment-providers': ['read', 'create', 'update', 'delete'],
    'rbac-management': ['read', 'update'],
    'admin-users': ['read', 'create', 'update', 'delete'],
};

const ADMIN_PAGES = [
    '/admin',
    '/admin/products',
    '/admin/customers',
    '/admin/orders',
    '/admin/vendors',
    '/admin/couriers',
    '/admin/extra-products',
    '/admin/accounting',
    '/admin/taxonomy',
    '/admin/settings',
    '/admin/delivery-areas',
    '/admin/coupons',
    '/admin/special-dates',
    '/admin/banners',
    '/admin/reviews',
    '/admin/notifications',
    '/admin/pages',
    '/admin/payment-providers',
    '/admin/rbac-management',
    '/admin/admin-users',
];

const ADMIN_MENU_ITEMS = [
    'dashboard',
    'products',
    'extra-products',
    'orders',
    'customers',
    'vendors',
    'couriers',
    'delivery-areas',
    'accounting',
    'taxonomy',
    'coupons',
    'special-dates',
    'banners',
    'reviews',
    'notifications',
    'pages',
    'payment-providers',
    'settings',
    'rbac-management',
    'admin-users',
];

const loadPanelUser = async (uid: string, emailFallback: string): Promise<AuthStoreUser> => {
    const userDocRef = doc(db, 'users', uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
        throw new Error('Bu panel için yetkiniz bulunmamaktadır');
    }

    const userData = userDocSnap.data();
    const allowedRoles: UserRole[] = ['super_admin', 'admin', 'vendor'];
    const userRole: UserRole = userData.role as UserRole;

    if (!allowedRoles.includes(userRole)) {
        throw new Error('Bu panel için yetkiniz bulunmamaktadır');
    }

    if (userRole === 'admin' || userRole === 'super_admin') {
        return {
            uid,
            email: userData.email || emailFallback,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            phone: userData.phone || '',
            role: userRole,
            name: userData.firstName && userData.lastName
                ? `${userData.firstName} ${userData.lastName}`
                : userData.name || userData.firstName || userData.lastName || emailFallback.split('@')[0],
            permissions: ADMIN_PERMISSIONS,
            pages: ADMIN_PAGES,
            menuItems: ADMIN_MENU_ITEMS,
        };
    }

    const roleConfig = await rbacApi.getCurrentRoleConfig();

    return {
        uid,
        email: userData.email || emailFallback,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phone: userData.phone || '',
        role: userRole,
        name: userData.firstName && userData.lastName
            ? `${userData.firstName} ${userData.lastName}`
            : userData.firstName || userData.lastName || emailFallback.split('@')[0],
        permissions: roleConfig.permissions || {},
        pages: roleConfig.pages || [],
        menuItems: roleConfig.menuItems || [],
    };
};

export const useAuthStore = create<AuthStore>((set, get) => ({
    user: null,
    isLoading: true,
    isAuthenticated: false,
    idToken: null,

    login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
            console.log('🔐 Starting login process...', { email });

            // Firebase Authentication ile giriş yap
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;
            const idToken = await firebaseUser.getIdToken();
            localStorage.setItem('authToken', idToken);

            console.log('✅ Firebase auth successful', {
                uid: firebaseUser.uid,
                email: firebaseUser.email
            });

            try {
                const authUser = await loadPanelUser(firebaseUser.uid, firebaseUser.email || email);

                console.log('📝 Setting user state from Firestore:', authUser);

                // localStorage'a token, role, uid ve email kaydet
                localStorage.setItem('authUser', JSON.stringify(authUser));
                localStorage.setItem('user_role', authUser.role);
                localStorage.setItem('user_uid', firebaseUser.uid);
                localStorage.setItem('user_email', firebaseUser.email || email);

                set({
                    user: authUser,
                    idToken,
                    isAuthenticated: true,
                    isLoading: false,
                });

                console.log('✅ Login successful - Auth state updated:', { isAuthenticated: true, role: authUser.role });
            } catch (firestoreError: any) {
                console.error('❌ Firestore error:', firestoreError.message);
                await signOut(auth);
                clearStoredAuth();
                set({ isLoading: false });
                throw new Error('Kullanıcı bilgileri alınamadı');
            }
        } catch (error: any) {
            console.error('❌ Login error:', error);
            set({ isLoading: false });
            throw new Error(error.message || 'Giriş başarısız');
        }
    },

    logout: async () => {
        try {
            await signOut(auth);
            clearStoredAuth();
            set({
                user: null,
                idToken: null,
                isAuthenticated: false,
            });
            console.log('✅ Logout successful');
        } catch (error) {
            console.error('❌ Logout error:', error);
            throw error;
        }
    },

    setUser: (user: AuthStoreUser | null) => {
        set({
            user,
            isAuthenticated: !!user,
        });
        if (user) {
            localStorage.setItem('authUser', JSON.stringify(user));
        } else {
            localStorage.removeItem('authUser');
        }
    },

    hasPermission: (requiredRole: UserRole) => {
        const { user } = get();
        if (!user) return false;

        if (requiredRole === 'admin') {
            return user.role === 'admin' || user.role === 'super_admin';
        }
        if (requiredRole === 'vendor') {
            return user.role === 'vendor' || user.role === 'admin' || user.role === 'super_admin';
        }
        return true;
    },

    canAccessPage: (page: string) => {
        const { user, idToken } = get();

        // Token yoksa erişim yok
        if (!idToken) {
            return false;
        }

        if (!user) return false;

        // Admin & Super Admin = full access
        if (user.role === 'admin' || user.role === 'super_admin') return true;

        // localStorage'dan role kontrol et (fallback)
        const storedRole = localStorage.getItem('user_role');
        if (storedRole === 'admin' || storedRole === 'super_admin') return true;

        // Check if page is in user's allowed pages
        return user.pages ? user.pages.includes(page) : false;
    },

    hasResourcePermission: (resource: string, action: string) => {
        const { user, idToken } = get();

        // Token yoksa erişim yok
        if (!idToken) {
            return false;
        }

        if (!user) return false;

        // Admin & Super Admin = full access
        if (user.role === 'admin' || user.role === 'super_admin') return true;

        // localStorage'dan role kontrol et (fallback)
        const storedRole = localStorage.getItem('user_role');
        if (storedRole === 'admin' || storedRole === 'super_admin') return true;

        // Check user's permissions
        const resourcePermissions = user.permissions?.[resource];
        if (!resourcePermissions) return false;

        return resourcePermissions.includes(action);
    },

    initAuth: () => {
        console.log('🔄 Initializing auth state...');
        set({ isLoading: true });

        // İlk olarak localStorage'dan oku
        const savedUser = localStorage.getItem('authUser');
        const savedToken = localStorage.getItem('authToken');
        const storedUid = localStorage.getItem('user_uid');

        if (savedUser && savedToken) {
            try {
                const authUser = JSON.parse(savedUser) as AuthStoreUser;

                // Token ve uid eşleşip eşleşmediğini kontrol et
                if (storedUid === authUser.uid) {
                    set({
                        user: authUser,
                        idToken: savedToken,
                        isAuthenticated: true,
                        isLoading: false,
                    });
                } else {
                    // UID eşleşmiyor - logout yapı
                    console.warn('⚠️ UID mismatch, clearing auth');
                    clearStoredAuth();
                    set({ isLoading: false });
                }
            } catch (error) {
                console.error('❌ Failed to parse saved user:', error);
                clearStoredAuth();
                set({ isLoading: false });
            }
        } else {
            // localStorage'da token yok - logout
            clearStoredAuth();
            set({ isLoading: false });
        }

        // Firebase Auth state değişikliklerini dinle (logout vb. için)
        onAuthStateChanged(auth, async (firebaseUser) => {
            console.log('🔄 Firebase auth state changed:', {
                isLoggedIn: !!firebaseUser,
                uid: firebaseUser?.uid,
            });

            if (!firebaseUser) {
                const hadStoredSession = !!localStorage.getItem('authToken');
                console.log('🚪 User logged out');
                clearStoredAuth();
                set({
                    user: null,
                    idToken: null,
                    isAuthenticated: false,
                });

                // Elde eski bir oturum varken Firebase null döndüyse oturum süresi dolmuş demektir.
                // Login sayfasına sert yönlendirme yap (menülerin açık kalmasını ve boş veri hatalarını engeller).
                if (hadStoredSession && window.location.pathname !== '/login') {
                    await handleUnauthorizedSession();
                }
                return;
            }

            try {
                const freshToken = await firebaseUser.getIdToken();
                localStorage.setItem('authToken', freshToken);

                const authUser = await loadPanelUser(firebaseUser.uid, firebaseUser.email || '');

                localStorage.setItem('authUser', JSON.stringify(authUser));
                localStorage.setItem('user_role', authUser.role);
                localStorage.setItem('user_uid', authUser.uid);
                localStorage.setItem('user_email', authUser.email);

                set({
                    user: authUser,
                    idToken: freshToken,
                    isAuthenticated: true,
                    isLoading: false,
                });
                console.log('✅ Auth state updated for user:', authUser.email);
            } catch (error) {
                console.error('❌ Failed to load user data on auth change:', error);
                set({ isLoading: false });
            }
        });

        // Firebase ID Token yenilenmelerini dinle (60 dk'da bir otomatik yenilenir)
        onIdTokenChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                try {
                    const freshToken = await firebaseUser.getIdToken();
                    localStorage.setItem('authToken', freshToken);
                    set({ idToken: freshToken });
                    console.log('🔄 [AuthStore] Firebase ID token refreshed');
                } catch (err) {
                    console.warn('⚠️ [AuthStore] Failed to get refreshed token:', err);
                }
            }
        });

        // Token refresh listener - Firebase SDK'nın otomatik refresh'ini yakala
        // Firebase SDK, token expire olmadan önce (~55 dakika) otomatik olarak 
        // refresh token kullanarak yeni ID token alır. Bu listener o anı yakalar.
        onIdTokenChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const currentUser = get().user;

                // Sadece authenticated kullanıcılar için işlem yap
                if (currentUser && get().isAuthenticated) {
                    try {
                        // Firebase SDK zaten refresh token kullanarak yenilemiş
                        // Biz sadece yeni token'ı alıp kaydediyoruz
                        const newToken = await firebaseUser.getIdToken();

                        console.log('✅ Token otomatik yenilendi (Firebase SDK refresh token kullandı)');
                        localStorage.setItem('authToken', newToken);
                        set({ idToken: newToken });
                    } catch (error) {
                        console.error('❌ Token alma hatası:', error);
                        toast.error('Oturum süreniz doldu. Lütfen tekrar giriş yapın.');
                        await handleUnauthorizedSession();
                    }
                }
            }
        });
    },
}));